import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { LOGGER_CACHE_DATA_KEY } from 'src/common/constants';
import { Environment } from 'src/Environment';
import {
  Application,
  ApplicationAction,
  IFeesService,
  ISalesforceService,
  Wizard,
  WizardContextProvider,
  useCatalogue,
} from 'src/features/common';
import TechnicalError from 'src/features/common/components/technical-error/technical-error';
import { ILoggingService } from 'src/features/common/services/logging-service';
import { ApplicationProvider } from 'src/features/device-activation/context/application-context';
import { IDeviceActivationTealiumService } from 'src/services/tealium/device-activation/device-activation-tealium-service';
import { IDpoTealiumService } from 'src/services/tealium/dpo/dpo-tealium-service';
import { AppCacheKeys } from 'src/types';
import { THEME, ThemeProvider } from '@vfs-digital-channels/ns-react-components';

export interface DeviceActivationAppProps {
  basePath: string;
  application: Application;
  pageNameMap: Map<string, React.ReactFragment>;
  salesforceService: ISalesforceService;
  dispatch: React.Dispatch<ApplicationAction>;
  feesService: IFeesService;
  deviceActivationTealiumService: IDeviceActivationTealiumService;
  dpoTealiumService: IDpoTealiumService;
  loggingService: ILoggingService;
}

export function DeviceActivationApp(props: DeviceActivationAppProps) {
  const [catalogue] = useCatalogue(props.salesforceService, props.feesService, props.application);

  const theme = React.useMemo(
    (): THEME => (!Environment.current.disabledFeatures.revamp ? THEME.revamp : THEME.light),
    [],
  );

  /**
   * logging cache
   * generate a new traceId if there's no one generated or I come to device-activation app from a different app
   */
  let loggerCacheData = JSON.parse(sessionStorage.getItem(LOGGER_CACHE_DATA_KEY) || '{}');
  if (
    Object.keys(loggerCacheData).length === 0 ||
    loggerCacheData.app !== AppCacheKeys.deviceActivation
  ) {
    loggerCacheData = {
      traceId: uuidv4(),
      app: AppCacheKeys.deviceActivation,
    };
    sessionStorage.setItem(LOGGER_CACHE_DATA_KEY, JSON.stringify(loggerCacheData));
  }

  return (
    <ThemeProvider defaultTheme={theme}>
      <TechnicalError>
        <ApplicationProvider
          application={props.application}
          dispatch={props.dispatch}
          salesforceService={props.salesforceService}
          catalogue={catalogue}
          deviceActivationTealiumService={props.deviceActivationTealiumService}
          dpoTealiumService={props.dpoTealiumService}
          loggingService={props.loggingService}
        >
          <WizardContextProvider>
            <Wizard
              application={props.application}
              pageNameMap={props.pageNameMap}
              basePath={props.basePath}
              dispatch={props.dispatch}
              automationIdPrefix="Device_Activation_POS_Light"
              dpo={!Environment.current.disabledFeatures.dpo}
            />
          </WizardContextProvider>
        </ApplicationProvider>
      </TechnicalError>
    </ThemeProvider>
  );
}
